import { Form, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useContext, useMemo } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import type { PriceValidationError } from 'app/components/pages/Experiences/Common/hook/useValidationStore';
import { useValidationErrors } from 'app/components/pages/Experiences/Common/hook/useValidationStore';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { capitalizeFirstLetter } from 'app/utils/string';

import type { RowData } from '../types';
import { buildFieldName } from '../utils';

export const useDayColumns = (days: DayOfWeek[]): ColumnsType<RowData> => {
  const { isViewMode, isEditMode } = useContext(ViewEditModeContext);
  const { currencySymbol } = useContext(CurrencyContext);
  const { priceError } = useValidationErrors();

  const form = Form.useFormInstance();
  const formValues = Form.useWatch([], form);

  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );

  const openingConfiguration = Form.useWatch('openingConfiguration', form);

  return useMemo(
    () =>
      days.map((day) => {
        const isClosed =
          openingConfiguration?.stockConfiguration?.days?.[day]?.isClosed ??
          false;

        const isDisabled = isViewMode;

        return {
          title: capitalizeFirstLetter(day),
          dataIndex: day.toLocaleLowerCase(),
          key: day.toLocaleLowerCase(),
          width: 120,
          className: isClosed && 'price-table-day-closed',
          render: (_, record: RowData) => {
            const fieldName = buildFieldName(record, day);

            const isAlwaysIncludedDisabled =
              (record.type === 'Night stay' && isAlwaysIncludedWithNight) ||
              isDisabled;

            const fieldValue = Array.isArray(fieldName)
              ? fieldName.reduce((obj, key) => obj?.[key], formValues)
              : formValues[fieldName];

            const dayIsOnError = (
              priceError.errors as PriceValidationError[]
            ).some((err) => {
              return (
                err.type === OPENING_CONFIGURATION_TYPE.DAILY &&
                err.fieldsByDay &&
                err.fieldsByDay[day] &&
                err.fieldsByDay[day].includes(
                  Array.isArray(fieldName)
                    ? fieldName[fieldName.length - 1]
                    : fieldName
                )
              );
            });

            return (
              <Form.Item name={fieldName}>
                {isEditMode ? (
                  <InputNumber
                    type="number"
                    disabled={isAlwaysIncludedDisabled}
                    addonAfter={currencySymbol}
                    className="price-input"
                    status={dayIsOnError ? 'error' : ''}
                    min={0}
                  />
                ) : (
                  <span className="view-data">{fieldValue || 'N/A'}</span>
                )}
              </Form.Item>
            );
          },
        };
      }),
    [
      days,
      openingConfiguration?.stockConfiguration?.days,
      isViewMode,
      isAlwaysIncludedWithNight,
      formValues,
      priceError.errors,
      isEditMode,
      currencySymbol,
    ]
  );
};
